import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { useNavItems } from '../../../hooks';
import { useDesignatedOperators } from '../../Profile/hooks/useDesignatedOperators';
import { useUser } from '../../../hooks/useUser';
import { 
  fetchCrrData, 
  submitCrrData, 
  requestCrrHtmlReport, 
  CrrDataParams, 
  CrrSubmissionPayload, 
  CrrHtmlReportParams 
} from './hooks';
import { CRRTable, CN61CrrHtmlTable } from './components';
import NotificationSnackbar from '../../../common/components/Snackbar/Snackbar';
import { NoDataFound } from '../../../common/components/NoDataFound';
import { IpcCn61ExtraData, convertCn61ExtraDataToInputFormat } from '../../../utils/ipcCrrCsvUtils';
import {
  MainContent,
  ReportContainer,
  SectionTitle,
  ButtonContainer,
  FilterSection,
  SelectContainer,
  StyledSegmentedControl,
  StyledSegmentButton,
  ClearResultsButton,
  ExportButton,
} from './CN61CompoundRateReports.styled';

interface DirectionOption {
  id: string;
  label: string;
}

interface CrrReportFilters {
  ppoValue: string;
  direction: string;
  year: string;
}

// Generate year options from 2008 to current year
const generateYearOptions = () => {
  const options: { value: string; label: string }[] = [];
  const currentYear = new Date().getFullYear();

  for (let year = currentYear; year >= 2008; year--) {
    options.push({
      value: year.toString(),
      label: year.toString(),
    });
  }

  return options;
};

function CN61CrrPage() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter states - Initialize from URL params if available
  const [filters, setFilters] = useState<CrrReportFilters>(() => {
    const ppo = searchParams.get('ppo') || '';
    const year = searchParams.get('year') || '';
    const direction = searchParams.get('direction') || 'Inbound';
    
    return {
      ppoValue: ppo,
      direction: direction,
      year: year,
    };
  });

  // Check if we came from a redirect (has URL params)
  const isFromRedirect = useMemo(() => {
    return !!(searchParams.get('ppo') && searchParams.get('year'));
  }, [searchParams]);

  // Results state
  const [noDataFound, setNoDataFound] = useState<boolean>(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);
  
  // Track if user has loaded CRR data
  const [hasCrrData, setHasCrrData] = useState<boolean>(false);
  
  // CRR data state - will be populated from API response
  const [crrData, setCrrData] = useState<any>(null);
  
  // CRR HTML report data state
  const [crrHtmlData, setCrrHtmlData] = useState<any>(null);

  // CN61 extra data from CSV uploads (fields like Postcards, Registered, etc.)
  const [cn61ExtraData, setCn61ExtraData] = useState<IpcCn61ExtraData[]>([]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'warning' | 'info' | 'success',
  });

  // Alert state for 400 errors
  const [showIncompleteDataAlert, setShowIncompleteDataAlert] = useState<boolean>(false);

  // Fetch designated operators for PPO dropdown
  const {
    data: designatedOperatorsData,
    isLoading: isPpoLoading,
    error: ppoError,
  } = useDesignatedOperators({ size: 300 });

  // Get current user info for CSV functionality
  const { data: userInfo } = useUser();

  // Generate year options
  const yearOptions = useMemo(() => generateYearOptions(), []);

  // Direction filter options
  const directionOptions: DirectionOption[] = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
    { id: 'All', label: 'All' },
  ];

  // Map the designated operators to PPO options
  const ppoOptions = React.useMemo(() => {
    const options: { id: string; label: string; hasLicense?: boolean }[] = [];

    if (designatedOperatorsData?.content) {
      designatedOperatorsData.content
        .filter((operator) => !operator.disabled)
        .forEach((operator) => {
          options.push({
            id: operator.name,
            label: operator.name,
            hasLicense: operator.longName !== null,
          });
        });
    }

    return options;
  }, [designatedOperatorsData]);

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/cn61-crr');
  };

  const handleDirectionChange = (id: string) => {
    setFilters((prev) => ({ ...prev, direction: id }));
  };

  const handlePpoChange = (value: string) => {
    setFilters((prev) => ({ ...prev, ppoValue: value }));
  };

  const handleYearChange = (value: string) => {
    setFilters((prev) => ({ ...prev, year: value }));
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const showSnackbar = (
    message: string,
    severity: 'error' | 'warning' | 'info' | 'success' = 'error'
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleReset = () => {
    setFilters({
      ppoValue: '',
      direction: 'Inbound',
      year: '',
    });
    setNoDataFound(false);
    setCrrData(null);
    setCrrHtmlData(null);
    setHasCrrData(false);
    setShowIncompleteDataAlert(false);
    setSearchParams(new URLSearchParams());
  };

  // Helper function to format number to 3 decimal places as number
  const formatToThreeDecimals = (value: string | null): number => {
    if (!value || value === '') return 0.000;
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 0.000;
    
    return parseFloat(numValue.toFixed(3));
  };

  // Transform CRR data for API submission
  const transformCrrDataForSubmission = (crrData: any): CrrSubmissionPayload => {
    const payload: CrrSubmissionPayload = {};

    if (crrData?.command?.inbound) {
      payload.inbound = {
        priority: [],
        nonPriority: []
      };

      // Transform priority data
      if (crrData.command.inbound.priority) {
        payload.inbound.priority = crrData.command.inbound.priority.map((item: any) => ({
          remunerationGroup: {
            id: item.remunerationGroup.id || 0,
            name: item.remunerationGroup.name || '',
            operator: (item.remunerationGroup.operator && 
                      item.remunerationGroup.operator.name && 
                      item.remunerationGroup.operator.name.trim() !== '') ? {
              id: item.remunerationGroup.operator.id || 0,
              name: item.remunerationGroup.operator.name || '',
              longName: item.remunerationGroup.operator.longName || '',
              disabled: item.remunerationGroup.operator.disabled || false
            } : null,
            displayName: item.remunerationGroup.displayName || '',
            predefined: item.remunerationGroup.predefined || false,
            homeCountry: item.remunerationGroup.homeCountry || false
          },
          formatP: {
            kg: formatToThreeDecimals(item.formatP?.tdKg),
            item: formatToThreeDecimals(item.formatP?.tdItem)
          },
          formatG: {
            kg: formatToThreeDecimals(item.formatG?.tdKg),
            item: formatToThreeDecimals(item.formatG?.tdItem)
          },
          formatE: {
            kg: formatToThreeDecimals(item.formatE?.tdKg),
            item: formatToThreeDecimals(item.formatE?.tdItem)
          }
        }));
      }

      // Transform non-priority data
      if (crrData.command.inbound.nonPriority) {
        payload.inbound.nonPriority = crrData.command.inbound.nonPriority.map((item: any) => ({
          remunerationGroup: {
            id: item.remunerationGroup.id || 0,
            name: item.remunerationGroup.name || '',
            operator: (item.remunerationGroup.operator && 
                      item.remunerationGroup.operator.name && 
                      item.remunerationGroup.operator.name.trim() !== '') ? {
              id: item.remunerationGroup.operator.id || 0,
              name: item.remunerationGroup.operator.name || '',
              longName: item.remunerationGroup.operator.longName || '',
              disabled: item.remunerationGroup.operator.disabled || false
            } : null,
            displayName: item.remunerationGroup.displayName || '',
            predefined: item.remunerationGroup.predefined || false,
            homeCountry: item.remunerationGroup.homeCountry || false
          },
          formatP: {
            kg: formatToThreeDecimals(item.formatP?.tdKg),
            item: formatToThreeDecimals(item.formatP?.tdItem)
          },
          formatG: {
            kg: formatToThreeDecimals(item.formatG?.tdKg),
            item: formatToThreeDecimals(item.formatG?.tdItem)
          },
          formatE: {
            kg: formatToThreeDecimals(item.formatE?.tdKg),
            item: formatToThreeDecimals(item.formatE?.tdItem)
          }
        }));
      }
    }

    if (crrData?.command?.outbound) {
      payload.outbound = {
        priority: [],
        nonPriority: []
      };

      // Transform priority data
      if (crrData.command.outbound.priority) {
        payload.outbound.priority = crrData.command.outbound.priority.map((item: any) => ({
          remunerationGroup: {
            id: item.remunerationGroup.id || 0,
            name: item.remunerationGroup.name || '',
            operator: (item.remunerationGroup.operator && 
                      item.remunerationGroup.operator.name && 
                      item.remunerationGroup.operator.name.trim() !== '') ? {
              id: item.remunerationGroup.operator.id || 0,
              name: item.remunerationGroup.operator.name || '',
              longName: item.remunerationGroup.operator.longName || '',
              disabled: item.remunerationGroup.operator.disabled || false
            } : null,
            displayName: item.remunerationGroup.displayName || '',
            predefined: item.remunerationGroup.predefined || false,
            homeCountry: item.remunerationGroup.homeCountry || false
          },
          formatP: {
            kg: formatToThreeDecimals(item.formatP?.tdKg),
            item: formatToThreeDecimals(item.formatP?.tdItem)
          },
          formatG: {
            kg: formatToThreeDecimals(item.formatG?.tdKg),
            item: formatToThreeDecimals(item.formatG?.tdItem)
          },
          formatE: {
            kg: formatToThreeDecimals(item.formatE?.tdKg),
            item: formatToThreeDecimals(item.formatE?.tdItem)
          }
        }));
      }

      // Transform non-priority data
      if (crrData.command.outbound.nonPriority) {
        payload.outbound.nonPriority = crrData.command.outbound.nonPriority.map((item: any) => ({
          remunerationGroup: {
            id: item.remunerationGroup.id || 0,
            name: item.remunerationGroup.name || '',
            operator: (item.remunerationGroup.operator && 
                      item.remunerationGroup.operator.name && 
                      item.remunerationGroup.operator.name.trim() !== '') ? {
              id: item.remunerationGroup.operator.id || 0,
              name: item.remunerationGroup.operator.name || '',
              longName: item.remunerationGroup.operator.longName || '',
              disabled: item.remunerationGroup.operator.disabled || false
            } : null,
            displayName: item.remunerationGroup.displayName || '',
            predefined: item.remunerationGroup.predefined || false,
            homeCountry: item.remunerationGroup.homeCountry || false
          },
          formatP: {
            kg: formatToThreeDecimals(item.formatP?.tdKg),
            item: formatToThreeDecimals(item.formatP?.tdItem)
          },
          formatG: {
            kg: formatToThreeDecimals(item.formatG?.tdKg),
            item: formatToThreeDecimals(item.formatG?.tdItem)
          },
          formatE: {
            kg: formatToThreeDecimals(item.formatE?.tdKg),
            item: formatToThreeDecimals(item.formatE?.tdItem)
          }
        }));
      }
    }

    return payload;
  };

  const handleExport = async (format: 'html' | 'pdf' | 'xls') => {
    // Validate required fields
    if (!filters.year) {
      showSnackbar('Please select a Year');
      return;
    }

    if (!filters.ppoValue) {
      showSnackbar('Please select a PPO');
      return;
    }

    // Validate CRR data
    if (!crrData) {
      showSnackbar('Please load CRR data first by clicking "Load CRR Data"');
      return;
    }

    const hasValidCRRData = validateCRRData(crrData);
    if (!hasValidCRRData) {
      showSnackbar('Please fill all CRR TD rates with exactly 3 decimals before exporting');
      return;
    }

    // Show loading state
    setIsGeneratingReport(true);

    try {
      // Transform CRR data for submission
      const crrPayload = transformCrrDataForSubmission(crrData);
      const crrHtmlParams: CrrHtmlReportParams = {
        ppo: filters.ppoValue,
        year: filters.year,
        direction: filters.direction.toUpperCase(),
        formatType: format
      };

      const response = await requestCrrHtmlReport(crrHtmlParams, crrPayload);

      if (response.ok) {
        if (format === 'html') {
          try {
            const jsonData = await response.json();
            setCrrHtmlData(jsonData);
            showSnackbar(`CRR ${format.toUpperCase()} report loaded successfully!`, 'success');
          } catch (jsonError) {
            showSnackbar('Failed to parse CRR HTML response data', 'error');
            return;
          }
        } else {
          // For PDF/XLS, trigger download
          // Get the blob
          const blob = await response.blob();
          
          // Validate blob size
          if (blob.size === 0) {
            showSnackbar('Received empty file from server', 'error');
            return;
          }
          
          // Extract filename from Content-Disposition header
          const contentDisposition = response.headers.get('Content-Disposition');
          let filename = `crr-report-${filters.ppoValue}-${filters.year}.${format.toLowerCase()}`;
          
          if (contentDisposition) {
            const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
            if (filenameMatch && filenameMatch[2]) {
              filename = decodeURIComponent(filenameMatch[2]);
            }
          }
          
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          
          // Cleanup after a short delay
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 100);
          
          showSnackbar(`CRR ${format.toUpperCase()} report exported successfully!`, 'success');
        }
      } else {
        // Handle specific error cases
        if (response.status === 400) {
          setShowIncompleteDataAlert(true);
          
          return;
        }
        
        const errorText = await response.text();
        showSnackbar(`Failed to generate CRR report: ${errorText}`, 'error');
      }
    } catch (error) {
      showSnackbar(`Error generating CRR report: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleLoadCrrData = async () => {
    // Validate required fields
    if (!filters.year) {
      showSnackbar('Please select a Year');
      return;
    }

    if (!filters.ppoValue) {
      showSnackbar('Please select a PPO');
      return;
    }

    // Show loading state
    setIsGeneratingReport(true);

    try {
      // For CRR, fetch data using the /cn61/crr/{ppo}/{year}/{direction} endpoint
      const crrParams: CrrDataParams = {
        ppo: filters.ppoValue,
        year: filters.year,
        direction: filters.direction.toUpperCase()
      };

      const response = await fetchCrrData(crrParams);

      if (response.ok) {
        try {
          const jsonData = await response.json();
          setCrrData(jsonData);
          setHasCrrData(true);
          setNoDataFound(false);
          showSnackbar('CRR data loaded successfully!', 'success');
        } catch (jsonError) {
          showSnackbar('Failed to parse CRR response data', 'error');
          return;
        }
      } else {
        const errorText = await response.text();
        if (response.status === 404) {
          setNoDataFound(true);
          showSnackbar('No CRR data found for the selected criteria', 'info');
        } else if (response.status === 400) {
          setNoDataFound(false);
          setShowIncompleteDataAlert(true);
         
        } else {
          showSnackbar(`Failed to load CRR data: ${errorText}`, 'error');
        }
        return;
      }
    } catch (error) {
      showSnackbar('An error occurred while loading the CRR data', 'error');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Manual retry function
  const handleRetry = () => {
    if (retryCount < 3) {
      setRetryCount(prev => prev + 1);
      handleLoadCrrData();
    } else {
      showSnackbar('Maximum retry attempts reached. Please check your connection and try again later.', 'error');
    }
  };

  const clearResults = useCallback(() => {
    setNoDataFound(false);
    setCrrData(null);
    setCrrHtmlData(null);
    setHasCrrData(false);
    setShowIncompleteDataAlert(false);
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Extract total rate values from CRR HTML data for CN61 REIMS
  const extractCrrTotalsForCn61 = useCallback((htmlData: any) => {
    const totals = {
      inbound: {
        formatP: { priority: { kg: 0, item: 0 }, nonPriority: { kg: 0, item: 0 } },
        formatG: { priority: { kg: 0, item: 0 }, nonPriority: { kg: 0, item: 0 } },
        formatE: { priority: { kg: 0, item: 0 }, nonPriority: { kg: 0, item: 0 } }
      },
      outbound: {
        formatP: { priority: { kg: 0, item: 0 }, nonPriority: { kg: 0, item: 0 } },
        formatG: { priority: { kg: 0, item: 0 }, nonPriority: { kg: 0, item: 0 } },
        formatE: { priority: { kg: 0, item: 0 }, nonPriority: { kg: 0, item: 0 } }
      }
    };

    if (!htmlData) return totals;

    // Extract from inbound data if available
    if (htmlData.inbound) {
      const inboundData = htmlData.inbound;
      
      // Priority data
      if (inboundData.pgePriority) {
        // P format totals
        if (inboundData.pgePriority.totalP) {
          totals.inbound.formatP.priority.kg = inboundData.pgePriority.totalP.rateKg || 0;
          totals.inbound.formatP.priority.item = inboundData.pgePriority.totalP.rateItem || 0;
        }
        // G format totals
        if (inboundData.pgePriority.totalG) {
          totals.inbound.formatG.priority.kg = inboundData.pgePriority.totalG.rateKg || 0;
          totals.inbound.formatG.priority.item = inboundData.pgePriority.totalG.rateItem || 0;
        }
        // E format totals
        if (inboundData.pgePriority.totalE) {
          totals.inbound.formatE.priority.kg = inboundData.pgePriority.totalE.rateKg || 0;
          totals.inbound.formatE.priority.item = inboundData.pgePriority.totalE.rateItem || 0;
        }
      }

      // Non-priority data
      if (inboundData.pgeNonPriority) {
        // P format totals
        if (inboundData.pgeNonPriority.totalP) {
          totals.inbound.formatP.nonPriority.kg = inboundData.pgeNonPriority.totalP.rateKg || 0;
          totals.inbound.formatP.nonPriority.item = inboundData.pgeNonPriority.totalP.rateItem || 0;
        }
        // G format totals
        if (inboundData.pgeNonPriority.totalG) {
          totals.inbound.formatG.nonPriority.kg = inboundData.pgeNonPriority.totalG.rateKg || 0;
          totals.inbound.formatG.nonPriority.item = inboundData.pgeNonPriority.totalG.rateItem || 0;
        }
        // E format totals
        if (inboundData.pgeNonPriority.totalE) {
          totals.inbound.formatE.nonPriority.kg = inboundData.pgeNonPriority.totalE.rateKg || 0;
          totals.inbound.formatE.nonPriority.item = inboundData.pgeNonPriority.totalE.rateItem || 0;
        }
      }
    }

    // Extract from outbound data if available
    if (htmlData.outbound) {
      const outboundData = htmlData.outbound;
      
      // Priority data
      if (outboundData.pgePriority) {
        // P format totals
        if (outboundData.pgePriority.totalP) {
          totals.outbound.formatP.priority.kg = outboundData.pgePriority.totalP.rateKg || 0;
          totals.outbound.formatP.priority.item = outboundData.pgePriority.totalP.rateItem || 0;
        }
        // G format totals
        if (outboundData.pgePriority.totalG) {
          totals.outbound.formatG.priority.kg = outboundData.pgePriority.totalG.rateKg || 0;
          totals.outbound.formatG.priority.item = outboundData.pgePriority.totalG.rateItem || 0;
        }
        // E format totals
        if (outboundData.pgePriority.totalE) {
          totals.outbound.formatE.priority.kg = outboundData.pgePriority.totalE.rateKg || 0;
          totals.outbound.formatE.priority.item = outboundData.pgePriority.totalE.rateItem || 0;
        }
      }

      // Non-priority data
      if (outboundData.pgeNonPriority) {
        // P format totals
        if (outboundData.pgeNonPriority.totalP) {
          totals.outbound.formatP.nonPriority.kg = outboundData.pgeNonPriority.totalP.rateKg || 0;
          totals.outbound.formatP.nonPriority.item = outboundData.pgeNonPriority.totalP.rateItem || 0;
        }
        // G format totals
        if (outboundData.pgeNonPriority.totalG) {
          totals.outbound.formatG.nonPriority.kg = outboundData.pgeNonPriority.totalG.rateKg || 0;
          totals.outbound.formatG.nonPriority.item = outboundData.pgeNonPriority.totalG.rateItem || 0;
        }
        // E format totals
        if (outboundData.pgeNonPriority.totalE) {
          totals.outbound.formatE.nonPriority.kg = outboundData.pgeNonPriority.totalE.rateKg || 0;
          totals.outbound.formatE.nonPriority.item = outboundData.pgeNonPriority.totalE.rateItem || 0;
        }
      }
    }

    return totals;
  }, []);

  // Handle Continue CN61 button click
  const handleContinueCn61 = useCallback(() => {
    if (!crrHtmlData) {
      showSnackbar('No CRR HTML data available');
      return;
    }

    // Extract totals from CRR HTML data
    const crrTotals = extractCrrTotalsForCn61(crrHtmlData);

    // Build the query parameters to pass to the REIMS page
    const queryParams = new URLSearchParams({
      ppo: filters.ppoValue,
      year: filters.year,
      direction: filters.direction,
      crrData: JSON.stringify(crrTotals), // Pass CRR totals as JSON string
    });

    // Include CN61 extra data if available (from CSV uploads)
    if (cn61ExtraData && cn61ExtraData.length > 0) {
      const cn61ExtraInput = convertCn61ExtraDataToInputFormat(cn61ExtraData);
      queryParams.set('cn61ExtraData', JSON.stringify(cn61ExtraInput));
    }

    // Navigate to CN61 REIMS page with CRR data
    navigate(`/cn61-compound-rate-reports/reims?${queryParams.toString()}`);
  }, [crrHtmlData, filters, extractCrrTotalsForCn61, navigate, showSnackbar, cn61ExtraData]);

  // Handle CRR data changes
  const handleCRRDataChange = useCallback((updatedData: any) => {
    setCrrData(updatedData);
  }, []);

  // Handle CN61 extra data changes (from CSV upload)
  const handleCn61ExtraDataChange = useCallback((extraData: IpcCn61ExtraData[]) => {
    setCn61ExtraData(extraData);
  }, []);

  // Track retry attempts
  const [retryCount, setRetryCount] = useState<number>(0);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState<boolean>(false);

  // Auto-load data when coming from redirect
  useEffect(() => {
    if (isFromRedirect && filters.ppoValue && filters.year && !hasCrrData && !isGeneratingReport && !hasAttemptedLoad && retryCount < 3) {
      setHasAttemptedLoad(true);
      setRetryCount(prev => prev + 1);
      handleLoadCrrData();
    }
  }, [isFromRedirect, filters.ppoValue, filters.year, hasCrrData, isGeneratingReport, hasAttemptedLoad, retryCount]);

  // Validate that all present fields are filled with exactly 3 decimals
  const threeDecimalsRegex = useMemo(() => /^\d+\.\d{3}$/, []);
  
  const isValueComplete = useCallback((val: unknown) => {
    if (val === null || val === undefined || val === '') return false;
    return threeDecimalsRegex.test(String(val));
  }, [threeDecimalsRegex]);

  // Validate direction data (inbound/outbound) - all fields must have exactly 3 decimals
  const validateDirectionData = useCallback((directionData: any) => {
    if (!directionData.priority && !directionData.nonPriority) return false;
    
    // Check if at least one priority type has data
    const hasPriorityData = directionData.priority && directionData.priority.length > 0;
    const hasNonPriorityData = directionData.nonPriority && directionData.nonPriority.length > 0;
    
    if (!hasPriorityData && !hasNonPriorityData) return false;
    
    // Validate all fields have exactly 3 decimals
    let allFieldsValid = true;
    
    const checkArray = (arr: any[] | undefined | null) => {
      if (!arr) return;
      arr.forEach((item) => {
        ['formatP', 'formatG', 'formatE'].forEach((fmt) => {
          const f = item[fmt];
          if (!f) return;
          if (!isValueComplete(f.tdKg) || !isValueComplete(f.tdItem)) {
            allFieldsValid = false;
          }
        });
      });
    };
    
    checkArray(directionData.priority);
    checkArray(directionData.nonPriority);
    
    return allFieldsValid;
  }, [isValueComplete]);

  // Validate CRR data - all fields must have exactly 3 decimals
  const validateCRRData = useCallback((data: any) => {
    if (!data || !data.command) return false;
    
    const { inbound, outbound } = data.command;
    
    // Check if at least one direction has data
    if (!inbound && !outbound) return false;
    
    // Validate inbound data if present
    if (inbound) {
      const hasValidInbound = validateDirectionData(inbound);
      if (!hasValidInbound) return false;
    }
    
    // Validate outbound data if present
    if (outbound) {
      const hasValidOutbound = validateDirectionData(outbound);
      if (!hasValidOutbound) return false;
    }
    
    return true;
  }, [validateDirectionData]);

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column' }}
      data-testid='cn61-crr-page-container'
    >
      <Grid container data-testid="cn61-crr-page-grid-container">
        {/* First Row - SectionBar */}
        <Grid item xs={12} data-testid="cn61-crr-page-section-bar-container">
          <SectionBar
            title='CN61 Compound Rate Reports (CRR)'
            subtitle='IPC TD Accounting'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            onSubtitleClick={handleSubtitleClick}
            data-testid='cn61-crr-page-section-bar'
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }} data-testid="cn61-crr-page-main-row">
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }} data-testid="cn61-crr-page-sidebar-container">
              <Sidebar
                isOpen={sidebarOpen}
                onToggle={handleSidebarToggle}
                navItems={navItems}
                data-testid='cn61-crr-page-sidebar'
              />
            </Box>
          )}

          {/* Main Content */}
          <MainContent
            sidebarOpen={sidebarOpen}
            data-testid='cn61-crr-page-main-content'
          >
            <ReportContainer data-testid='cn61-crr-page-content'>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
                data-testid='cn61-crr-page-header'
              >
                <Typography
                  variant='h4'
                  sx={{ fontWeight: 600, color: '#063142' }}
                  data-testid='cn61-crr-page-title'
                >
                  CN61 Compound Rate Reports (CRR)
                </Typography>
                <Box
                  sx={{ display: 'flex', gap: 1 }}
                  data-testid='cn61-crr-page-header-actions'
                >
                  <Button
                    variant='text'
                    onClick={() => navigate('/cn61-crr')}
                    sx={{
                      color: '#00ACE7',
                      fontSize: '12px',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#E3F7FE',
                      },
                    }}
                    data-testid='cn61-crr-page-back-to-main-btn'
                  >
                    ← Back to Main
                  </Button>
                  {(crrData || noDataFound || crrHtmlData) && (
                    <ClearResultsButton
                      variant='outlined'
                      onClick={clearResults}
                      data-testid='cn61-crr-page-clear-results-btn'
                    >
                      Clear Results
                    </ClearResultsButton>
                  )}
                </Box>
              </Box>

              {/* Incomplete Data Alert */}
              {showIncompleteDataAlert && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3,
                    '& .MuiAlert-message': {
                      fontSize: '14px',
                      fontWeight: 500
                    }
                  }}
                  onClose={() => setShowIncompleteDataAlert(false)}
                  data-testid="cn61-crr-page-incomplete-data-alert"
                >
                  The NO3 & CN56 Summary is incomplete and thus the CN61 REIMS/Interconnect cannot be generated. Please complete the NO3 & CN56 Summary (NET IPK/ADJ/Net %) and try it again.
                </Alert>
              )}

              {/* Main Filter Section - Only show if not from redirect */}
              {!isFromRedirect && (
                <>
                  {/* PPO Section */}
                  <FilterSection data-testid="cn61-crr-page-filter-section">
                  {/* PPO Dropdown and Direction Filter in one row */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2 }} data-testid="cn61-crr-page-ppo-direction-row">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="cn61-crr-page-ppo-container">
                      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }} data-testid="cn61-crr-page-ppo-label">
                        DO *
                      </Typography>
                      <SelectContainer data-testid="cn61-crr-page-ppo-select-container">
                        <FormControl size='small' fullWidth data-testid="cn61-crr-page-ppo-form-control">
                          <Select
                            value={filters.ppoValue}
                            onChange={(e) => handlePpoChange(e.target.value)}
                            displayEmpty
                            sx={{ minWidth: 160 }}
                            disabled={isPpoLoading}
                            data-testid="cn61-crr-page-ppo-select"
                          >
                            <MenuItem value='' data-testid="cn61-crr-page-ppo-default-option">
                              {isPpoLoading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="cn61-crr-page-ppo-loading">
                                  <CircularProgress size={16} data-testid="cn61-crr-page-ppo-loading-spinner" />
                                  <span data-testid="cn61-crr-page-ppo-loading-text">Loading...</span>
                                </Box>
                              ) : (
                                'All'
                              )}
                            </MenuItem>
                            {!isPpoLoading &&
                              !ppoError &&
                              ppoOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id} data-testid={`cn61-crr-page-ppo-option-${option.id}`}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      width: '100%',
                                    }}
                                    data-testid={`cn61-crr-page-ppo-option-content-${option.id}`}
                                  >
                                    <span data-testid={`cn61-crr-page-ppo-option-label-${option.id}`}>{option.label}</span>
                                    
                                  </Box>
                                </MenuItem>
                              ))}
                            {ppoError && <MenuItem disabled data-testid="cn61-crr-page-ppo-error">Error loading PPOs</MenuItem>}
                          </Select>
                        </FormControl>
                      </SelectContainer>
                    </Box>

                    {/* Direction Filter using Segmented Control */}
                    <StyledSegmentedControl data-testid="cn61-crr-page-direction-control">
                      {directionOptions.map((option) => (
                        <StyledSegmentButton
                          key={option.id}
                          active={filters.direction === option.id}
                          onClick={() => handleDirectionChange(option.id)}
                          sx={{
                            color: filters.direction === option.id ? '#ffffff' : '#00B6BA',
                            minWidth: '70px',
                          }}
                          data-testid={`cn61-crr-page-direction-${option.id}`}
                        >
                          {option.label}
                        </StyledSegmentButton>
                      ))}
                    </StyledSegmentedControl>
                  </Box>

                  {/* Divider */}
                  <Box
                    sx={{
                      height: '1px',
                      backgroundColor: '#E1E8ED',
                      mb: 2,
                    }}
                    data-testid="cn61-crr-page-divider-1"
                  />

                  {/* Year Selector */}
                  <Box sx={{ display: 'flex', gap: 2 }} data-testid="cn61-crr-page-year-section">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="cn61-crr-page-year-container">
                      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }} data-testid="cn61-crr-page-year-label">
                        Year: *
                      </Typography>
                      <SelectContainer data-testid="cn61-crr-page-year-select-container">
                        <FormControl size='small' fullWidth data-testid="cn61-crr-page-year-form-control">
                          <Select
                            value={filters.year}
                            onChange={(e) => handleYearChange(e.target.value)}
                            displayEmpty
                            sx={{ minWidth: 184 }}
                            data-testid="cn61-crr-page-year-select"
                          >
                            <MenuItem value='' data-testid="cn61-crr-page-year-default-option">Select Year</MenuItem>
                            {yearOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value} data-testid={`cn61-crr-page-year-option-${option.value}`}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </SelectContainer>
                    </Box>
                  </Box>

                  {/* Divider */}
                  <Box
                    sx={{
                      height: '1px',
                      backgroundColor: '#E1E8ED',
                      mb: 2,
                    }}
                    data-testid="cn61-crr-page-divider-2"
                  />

                  {/* CRR Section */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      gap: 2,
                    }}
                    data-testid="cn61-crr-page-crr-section"
                  >
                    <Box
                      sx={{
                        padding: 2,
                        minWidth: 0,
                      }}
                      data-testid="cn61-crr-page-crr-content"
                    >
                      <SectionTitle data-testid="cn61-crr-page-crr-title">Compound Rate Reports (CRR)</SectionTitle>
                      <Typography
                        variant='body2'
                        sx={{
                          color: '#666',
                          mt: 1,
                          fontSize: '14px',
                          mb: 2,
                        }}
                        data-testid="cn61-crr-page-crr-description"
                      >
                        Load and configure Compound Rate Reports data for CN61 processing
                      </Typography>
                      <Button
                        variant='outlined'
                        size='small'
                        onClick={handleLoadCrrData}
                        disabled={isGeneratingReport || !filters.year || !filters.ppoValue}
                        sx={{
                          borderColor: '#00ACE7',
                          color: '#00ACE7',
                          fontSize: '12px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          padding: '6px 16px',
                          mb: 2,
                          '&:hover': {
                            borderColor: '#0095C7',
                            backgroundColor: '#E3F7FE',
                          },
                          '&:disabled': {
                            borderColor: '#ccc',
                            color: '#ccc',
                          },
                        }}
                        data-testid="cn61-crr-page-load-crr-data-btn"
                      >
                        {isGeneratingReport ? <CircularProgress size={14} data-testid="cn61-crr-page-load-crr-data-spinner" /> : 'Load CRR Data'}
                      </Button>
                    </Box>
                  </Box>

                  {/* Action Buttons - Only show after CRR data is loaded */}
                  {hasCrrData && (
                    <ButtonContainer data-testid="cn61-crr-page-action-buttons">
                      <ExportButton
                        variant='contained'
                        onClick={() => handleExport('html')}
                        disabled={isGeneratingReport}
                        data-testid="cn61-crr-page-export-html-btn"
                      >
                        {isGeneratingReport ? (
                          <CircularProgress size={16} data-testid="cn61-crr-page-export-html-spinner" />
                        ) : (
                          'HTML'
                        )}
                      </ExportButton>
                      <ExportButton
                        variant='contained'
                        onClick={() => handleExport('pdf')}
                        disabled={isGeneratingReport}
                        data-testid="cn61-crr-page-export-pdf-btn"
                      >
                        {isGeneratingReport ? <CircularProgress size={16} data-testid="cn61-crr-page-export-pdf-spinner" /> : 'PDF'}
                      </ExportButton>
                      <ExportButton
                        variant='contained'
                        onClick={() => handleExport('xls')}
                        disabled={isGeneratingReport}
                        data-testid="cn61-crr-page-export-xls-btn"
                      >
                        {isGeneratingReport ? <CircularProgress size={16} data-testid="cn61-crr-page-export-xls-spinner" /> : 'XLS'}
                      </ExportButton>
                    </ButtonContainer>
                  )}

                  {/* Continue CN61 Button - Show when CRR HTML data is available (non-redirect) */}
                  {hasCrrData && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }} data-testid="cn61-crr-page-continue-cn61-section">
                      <Button
                        variant='contained'
                        onClick={handleContinueCn61}
                        sx={{
                          backgroundColor: '#00ACE7',
                          color: '#FFFFFF',
                          fontSize: '14px',
                          fontWeight: 600,
                          textTransform: 'none',
                          padding: '8px 24px',
                          '&:hover': {
                            backgroundColor: '#0095C7',
                          },
                        }}
                        data-testid="cn61-crr-page-continue-cn61-btn"
                      >
                        Continue CN61 →
                      </Button>
                    </Box>
                  )}
                </FilterSection>
              </>
              )}

              {/* Export Buttons - Show when data is loaded from redirect */}
              {isFromRedirect && hasCrrData && (
                <Box sx={{ mb: 3 }} data-testid="cn61-crr-page-redirect-export-buttons">
                  <ButtonContainer data-testid="cn61-crr-page-redirect-button-container">
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('html')}
                      disabled={isGeneratingReport}
                      data-testid="cn61-crr-page-redirect-export-html-btn"
                    >
                      {isGeneratingReport ? (
                        <CircularProgress size={16} data-testid="cn61-crr-page-redirect-export-html-spinner" />
                      ) : (
                        'HTML'
                      )}
                    </ExportButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('pdf')}
                      disabled={isGeneratingReport}
                      data-testid="cn61-crr-page-redirect-export-pdf-btn"
                    >
                      {isGeneratingReport ? <CircularProgress size={16} data-testid="cn61-crr-page-redirect-export-pdf-spinner" /> : 'PDF'}
                    </ExportButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('xls')}
                      disabled={isGeneratingReport}
                      data-testid="cn61-crr-page-redirect-export-xls-btn"
                    >
                      {isGeneratingReport ? <CircularProgress size={16} data-testid="cn61-crr-page-redirect-export-xls-spinner" /> : 'XLS'}
                    </ExportButton>
                  </ButtonContainer>
                </Box>
              )}

              {/* Continue CN61 Button - Show when CRR HTML data is available */}
              {crrHtmlData && (
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }} data-testid="cn61-crr-page-continue-cn61-redirect-section">
                  <Button
                    variant='contained'
                    onClick={handleContinueCn61}
                    sx={{
                      backgroundColor: '#00ACE7',
                      color: '#FFFFFF',
                      fontSize: '14px',
                      fontWeight: 600,
                      textTransform: 'none',
                      padding: '8px 24px',
                      '&:hover': {
                        backgroundColor: '#0095C7',
                      },
                    }}
                    data-testid="cn61-crr-page-continue-cn61-redirect-btn"
                  >
                    Continue CN61 →
                  </Button>
                </Box>
              )}

              {/* Retry Button - Show when from redirect and no data and retries available */}
              {isFromRedirect && !hasCrrData && !isGeneratingReport && retryCount < 3 && hasAttemptedLoad && (
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }} data-testid="cn61-crr-page-retry-section">
                  <Button
                    variant='outlined'
                    onClick={handleRetry}
                    sx={{
                      borderColor: '#00ACE7',
                      color: '#00ACE7',
                      '&:hover': {
                        borderColor: '#0095C7',
                        backgroundColor: '#E3F7FE',
                      },
                    }}
                    data-testid="cn61-crr-page-retry-btn"
                  >
                    Retry Loading CRR Data ({retryCount}/3)
                  </Button>
                </Box>
              )}

              {/* Loading indicator when from redirect */}
              {isFromRedirect && !hasCrrData && isGeneratingReport && (
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }} data-testid="cn61-crr-page-loading-section">
                  <CircularProgress size={20} data-testid="cn61-crr-page-loading-spinner" />
                  <Typography data-testid="cn61-crr-page-loading-text">Loading CRR data...</Typography>
                </Box>
              )}

              {/* No Data Found Section */}
              {noDataFound && (
                <NoDataFound
                  title='No CRR Data Available'
                  message='No Compound Rate Report data was found for the selected criteria. Please try adjusting your filters or selecting a different year.'
                  onClearResults={clearResults}
                  onResetFilters={handleReset}
                  data-testid="cn61-crr-page-no-data-found"
                />
              )}

              {/* Results Section - Show CRR tables when data exists */}
              {(crrData || crrHtmlData) && !noDataFound && (
                <Box sx={{ mt: 6 }} data-testid="cn61-crr-page-results-section">
                  <Box
                    sx={{
                      borderBottom: '2px solid #E1E8ED',
                      pb: 2,
                      mb: 4,
                    }}
                    data-testid="cn61-crr-page-results-divider"
                  ></Box>

                  {/* Results content */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="cn61-crr-page-results-content">
                    {/* CRR Table - Show when there's CRR data for editing */}
                    {crrData && (
                      <Box data-testid="cn61-crr-page-crr-table-section">
                        <CRRTable 
                          data={crrData} 
                          onDataChange={handleCRRDataChange}
                          onError={showSnackbar}
                          onSuccess={(message) => showSnackbar(message, 'success')}
                          currentUserPpo={userInfo?.designatedOperator}
                          onCn61ExtraDataChange={handleCn61ExtraDataChange}
                          data-testid="cn61-crr-page-crr-table"
                        />
                      </Box>
                    )}

                    {/* CRR HTML Report Table */}
                    {crrHtmlData && (
                      <Box data-testid="cn61-crr-page-crr-html-section">
                        {crrHtmlData.inbound && (
                          <CN61CrrHtmlTable 
                            data={crrHtmlData.inbound} 
                            title="CRR HTML Report - Inbound"
                            data-testid="cn61-crr-page-crr-html-inbound"
                          />
                        )}
                        {crrHtmlData.outbound && (
                          <CN61CrrHtmlTable 
                            data={crrHtmlData.outbound} 
                            title="CRR HTML Report - Outbound"
                            data-testid="cn61-crr-page-crr-html-outbound"
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid='cn61-crr-page-snackbar'
      />
    </Box>
  );
}

export default CN61CrrPage;